import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '@/services/api';
import { Logo } from '@/components/Logo';
import { LoadingSpinner } from '@/components/ui';

// Form validation schema
const resetPasswordSchema = z.object({
  new_password: z
    .string()
    .min(8, 'Password must be at least 8 characters'),
  confirm_password: z.string(),
}).refine((data) => data.new_password === data.confirm_password, {
  message: "Passwords don't match",
  path: ['confirm_password'],
});

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;

type PageState = 'form' | 'success' | 'error' | 'no-token';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [pageState, setPageState] = useState<PageState>('form');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const token = searchParams.get('token');

  // If no token in URL, show error state
  useEffect(() => {
    if (!token) {
      setPageState('no-token');
    }
  }, [token]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      new_password: '',
      confirm_password: '',
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (data: ResetPasswordForm) =>
      authApi.resetPassword({
        token: token || '',
        new_password: data.new_password,
        confirm_password: data.confirm_password,
      }),
    onSuccess: () => {
      setPageState('success');
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    },
    onError: (error: unknown) => {
      const apiError = error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      };
      const message = apiError.response?.data?.detail || 'Failed to reset password. Please try again.';
      setErrorMessage(message);
      setPageState('error');
    },
  });

  const onSubmit = async (data: ResetPasswordForm) => {
    resetPasswordMutation.mutate(data);
  };

  // No token state
  if (pageState === 'no-token') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full space-y-8" style={{ maxWidth: '384px', margin: '0 auto' }}>
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Logo variant="color" size="lg" className="!h-[64px] ml-6" />
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-lg px-8 py-10 text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Invalid reset link
            </h2>

            <p className="text-gray-600 mb-6">
              This password reset link is invalid. Please request a new password reset link.
            </p>

            <Link
              to="/forgot-password"
              className="inline-block w-full py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              style={{
                background: 'linear-gradient(90deg, #ff3160 0%, #cc1a40 50%, #ff3160 100%)',
              }}
            >
              Request new reset link
            </Link>

            <div className="mt-4">
              <Link
                to="/login"
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (pageState === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full space-y-8" style={{ maxWidth: '384px', margin: '0 auto' }}>
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Logo variant="color" size="lg" className="!h-[64px] ml-6" />
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-lg px-8 py-10 text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Password reset successful
            </h2>

            <p className="text-gray-600 mb-6">
              Your password has been reset successfully. You can now sign in with your new password.
            </p>

            <p className="text-sm text-gray-500 mb-6">
              Redirecting to sign in...
            </p>

            <Link
              to="/login"
              className="inline-block w-full py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              style={{
                background: 'linear-gradient(90deg, #ff3160 0%, #cc1a40 50%, #ff3160 100%)',
              }}
            >
              Sign in now
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (pageState === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full space-y-8" style={{ maxWidth: '384px', margin: '0 auto' }}>
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Logo variant="color" size="lg" className="!h-[64px] ml-6" />
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-lg px-8 py-10 text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Reset failed
            </h2>

            <p className="text-gray-600 mb-6">
              {errorMessage}
            </p>

            <Link
              to="/forgot-password"
              className="inline-block w-full py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
              style={{
                background: 'linear-gradient(90deg, #ff3160 0%, #cc1a40 50%, #ff3160 100%)',
              }}
            >
              Request new reset link
            </Link>

            <div className="mt-4">
              <button
                type="button"
                onClick={() => setPageState('form')}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full space-y-8" style={{ maxWidth: '384px', margin: '0 auto' }}>
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <Logo variant="color" size="lg" className="!h-[64px] ml-6" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Create new password
          </h2>
          <p className="mt-2 text-gray-600">
            Enter your new password below.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-4 bg-white shadow-lg rounded-lg px-8 py-10">
          <div>
            <label htmlFor="new_password" className="block text-sm font-medium text-gray-700">
              New Password
            </label>
            <input
              {...register('new_password')}
              type="password"
              id="new_password"
              className={`mt-1 appearance-none relative block w-full px-3 py-2 border rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm ${
                errors.new_password ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter new password"
              autoComplete="new-password"
              autoFocus
            />
            <div className="h-5 mt-1">
              {errors.new_password ? (
                <p className="text-sm text-red-600">{errors.new_password.message}</p>
              ) : (
                <p className="text-xs text-gray-500">Minimum 8 characters</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <input
              {...register('confirm_password')}
              type="password"
              id="confirm_password"
              className={`mt-1 appearance-none relative block w-full px-3 py-2 border rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm ${
                errors.confirm_password ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Confirm new password"
              autoComplete="new-password"
            />
            <div className="h-5 mt-1">
              {errors.confirm_password && (
                <p className="text-sm text-red-600">{errors.confirm_password.message}</p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || resetPasswordMutation.isPending}
            className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors ${
              isSubmitting || resetPasswordMutation.isPending
                ? 'cursor-not-allowed'
                : 'hover:opacity-90'
            }`}
            style={{
              background: isSubmitting || resetPasswordMutation.isPending
                ? '#fca5a5'
                : 'linear-gradient(90deg, #ff3160 0%, #cc1a40 50%, #ff3160 100%)',
            }}
          >
            {isSubmitting || resetPasswordMutation.isPending ? (
              <>
                <LoadingSpinner size="md" variant="white" className="-ml-1 mr-3" label="Resetting" />
                Resetting...
              </>
            ) : (
              'Reset password'
            )}
          </button>

          <div className="text-center pt-2">
            <Link
              to="/login"
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
